import type { ApplicationTabKey } from './applicationDetailTabStatus';
import type {
  ApplicationDeviceRiskResponseDto,
  ApplicationJourneyExecutionDto,
  ApplicationJourneyHeaderDto,
  ApplicationOnboardingSignalsDto,
  DecisionEngineJourneyDto,
  EmploymentReviewSignalsDto,
} from '../api/admin';
import type { EmployerFootprintAdminViewDto } from '../api/employerVerificationDashboardApi';
import type { IdentityLookupRow } from '../api/identityVerificationDashboardApi';
import { signalLabel } from './deviceIntelligenceCopy';
import { declineReasonPlain, uniquePlainIncomeFailReasons } from './applicationJourneyCopy';
import {
  presentIdentityAttempts,
  type IdentityAttemptPresentation,
} from './identityAttemptPresentation';
import { resolveDocumentAuthenticityOutcome } from './identityEvidenceQuestions';
import {
  formatDocumentExpiryDate,
  resolveIdentitySessionSummary,
} from './identitySessionSummary';
import { mitekAuthenticityOperatorCopy } from './mitekAuthenticitySignal';
import {
  hasMilestone as hasEmploymentMilestone,
  isEmploymentForwardedUnderReview,
  resolveEmploymentAdminPhase,
} from './employment/employmentJourneyStatus';
import { buildEmploymentUnderReviewNarrative } from './employment/employmentUnderReviewCopy';
import {
  normalizeLinkageStatus,
  resolveLinkageOverviewAlert,
  type LegacyLinkageJourney,
} from './legacyLinkageDisplay';

export type TabNarrativeTone = 'info' | 'success' | 'warning' | 'error';

export type TabNarrative = {
  headline: string;
  tone: TabNarrativeTone;
  /** Optional single supporting sentence (preferred over bullets for most tabs). */
  detail?: string | null;
  /** Short plain bullets — Device may use several; other tabs should leave empty. */
  bullets: string[];
  /** Worked calculation shown under the lines (PDL declines: the customer's own numbers). */
  calculation?: {
    rows: Array<{ label: string; value: string; kind?: 'minus' | 'result' | 'limit' }>;
    verdict: string;
  } | null;
  /** Where an outside reviewer should look next. Rendered as a link when navigation is available. */
  checkLink?: { label: string; tab: ApplicationTabKey } | null;
  /** Insider detail (codes, tiers, ratios) — collapsed under "Technical details". */
  technicalDetails?: string[];
};

const RISK_SIGNAL_KEYS = new Set([
  'vpn',
  'proxy',
  'bot',
  'virtual_machine',
  'tampering',
  'incognito',
  'high_activity_device',
  'developer_tools',
  'privacy_settings',
]);

function uniqueLabels(values: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const v of values) {
    const t = v.trim();
    if (!t || seen.has(t.toLowerCase())) continue;
    seen.add(t.toLowerCase());
    out.push(t);
  }
  return out;
}

function collectDeviceSignalLabels(data: ApplicationDeviceRiskResponseDto): string[] {
  const raw: string[] = [];
  for (const row of data.visitorApplicationHistory ?? []) {
    for (const sig of row.topDetectedSignals ?? []) raw.push(sig);
  }
  for (const a of data.assessments ?? []) {
    for (const s of a.detectedSignals ?? []) {
      if (s?.signal) raw.push(String(s.signal));
    }
    for (const reason of a.policyReasons ?? []) {
      const r = String(reason).toUpperCase();
      if (r === 'VPN') raw.push('vpn');
      if (r === 'PROXY') raw.push('proxy');
      if (r === 'BOT_BAD') raw.push('bot');
      if (r === 'VM') raw.push('virtual_machine');
      if (r === 'TAMPERING') raw.push('tampering');
      if (r === 'INCOGNITO') raw.push('incognito');
    }
  }
  return uniqueLabels(
    raw
      .map((s) => s.toLowerCase().replace(/\s+/g, '_'))
      .filter((s) => RISK_SIGNAL_KEYS.has(s))
      .map((s) => signalLabel(s)),
  );
}

function countOtherContacts(onboarding: ApplicationOnboardingSignalsDto | null | undefined): {
  otherEmails: number;
  otherPhones: number;
} {
  const inv = onboarding?.visitorContactInventory;
  return {
    otherEmails: (inv?.emails ?? []).filter((e) => !e.usedOnCurrentApplication).length,
    otherPhones: (inv?.phones ?? []).filter((p) => !p.usedOnCurrentApplication).length,
  };
}

/** Plain-language “what’s happening” for Device intelligence. */
export function buildDeviceTabNarrative(data: ApplicationDeviceRiskResponseDto): TabNarrative {
  const history = data.visitorApplicationHistory ?? [];
  const otherApps = history.filter((h) => !h.isCurrent);
  const otherCount = otherApps.length;
  const declinedOthers = otherApps.filter(
    (h) => Boolean(h.declineReason) || String(h.status).toUpperCase() === 'DECLINED',
  ).length;
  const { otherEmails, otherPhones } = countOtherContacts(data.onboarding);
  const differentContacts = otherEmails + otherPhones > 0;
  const signalLabels = collectDeviceSignalLabels(data);
  const agentDown = (data.assessments ?? []).some((a) =>
    (a.policyReasons ?? []).includes('FP_AGENT_UNAVAILABLE'),
  );
  const level = String(data.riskVerdict?.level ?? '').toLowerCase();
  const bullets: string[] = [];

  if (agentDown) {
    bullets.push(
      'Device checks could not finish completely — something went wrong while identifying the browser.',
    );
  }
  if (otherCount > 0) {
    bullets.push(
      otherCount === 1
        ? 'The same device was also used for 1 other loan application.'
        : `The same device was also used for ${otherCount} other loan applications.`,
    );
  }
  if (differentContacts) {
    const parts: string[] = [];
    if (otherEmails > 0) {
      parts.push(`${otherEmails} other email${otherEmails === 1 ? '' : 's'}`);
    }
    if (otherPhones > 0) {
      parts.push(`${otherPhones} other phone number${otherPhones === 1 ? '' : 's'}`);
    }
    bullets.push(
      `Those applications used different contact details (${parts.join(' and ')}) from this one — it may be more than one account.`,
    );
  }
  if (declinedOthers > 0) {
    bullets.push(
      declinedOthers === 1
        ? 'At least one of those other applications was declined.'
        : `${declinedOthers} of those other applications were declined.`,
    );
  }
  if (signalLabels.length > 0) {
    bullets.push(`Unusual connection or device signals showed up: ${signalLabels.join(', ')}.`);
  }
  if (
    !agentDown &&
    otherCount === 0 &&
    !differentContacts &&
    signalLabels.length === 0 &&
    level === 'clear'
  ) {
    bullets.push('No other applications or concerning device signals were found for this customer.');
  }

  let headline: string;
  let tone: TabNarrativeTone = 'info';

  if (agentDown && otherCount === 0 && signalLabels.length === 0 && !differentContacts) {
    headline = 'Device identity could not be fully verified on this application.';
    tone = 'warning';
  } else if (level === 'blocked') {
    headline =
      otherCount > 0
        ? differentContacts
          ? `This device was blocked — and the customer appears to have ${otherCount} other application${otherCount === 1 ? '' : 's'} with different contact details.`
          : `This device was blocked — and it is linked to ${otherCount} other application${otherCount === 1 ? '' : 's'}.`
        : 'This device was blocked based on risk signals.';
    tone = 'error';
  } else if (level === 'review' || otherCount > 0 || signalLabels.length > 0 || differentContacts) {
    const signalBit =
      signalLabels.length > 0
        ? ` Signals detected: ${signalLabels.slice(0, 3).join(', ')}.`
        : '';
    if (otherCount > 0 && differentContacts) {
      headline = `This customer has ${otherCount} other application${otherCount === 1 ? '' : 's'} from the same device, with different contact details.${signalBit}`;
    } else if (otherCount > 0 && signalLabels.length > 0) {
      headline = `Same device used for ${otherCount} other application${otherCount === 1 ? '' : 's'}, and risk signals (${signalLabels.slice(0, 3).join(', ')}) were detected.`;
    } else if (otherCount > 0) {
      headline = `Same device — the customer appears to have ${otherCount} other application${otherCount === 1 ? '' : 's'} from this device.`;
    } else if (signalLabels.length > 0) {
      headline = `Device needs a closer look — ${signalLabels.slice(0, 3).join(', ')} ${signalLabels.length === 1 ? 'was' : 'were'} detected.`;
    } else if (differentContacts) {
      headline = 'Different contact details showed up on applications from this same device.';
    } else {
      headline = 'Device signals need a closer look.';
    }
    tone = 'warning';
  } else if (level === 'observe') {
    headline = 'Device checks ran in observe mode — treat results as incomplete.';
    tone = 'info';
  } else {
    headline =
      'Device looks consistent for this application — no other accounts or concerning signals stood out.';
    tone = 'success';
  }

  return { headline, tone, bullets: bullets.slice(0, 5) };
}

/** Plain-language “what’s happening” for Account access. */
export function buildOnboardingTabNarrative(onboarding: ApplicationOnboardingSignalsDto): TabNarrative {
  const otherEmails = onboarding.visitorContactInventory.emails.filter((e) => !e.usedOnCurrentApplication);
  const otherPhones = onboarding.visitorContactInventory.phones.filter((p) => !p.usedOnCurrentApplication);
  const inventoryReview = [...otherEmails, ...otherPhones].filter((r) => r.needsReview);
  const reviewEvents = onboarding.contactEvents.filter((e) => e.needsReview);
  const c = onboarding.contactComparison;
  const bullets: string[] = [];

  if (otherEmails.length > 0 || otherPhones.length > 0) {
    const parts: string[] = [];
    if (otherEmails.length > 0) {
      parts.push(
        `${otherEmails.length} other email${otherEmails.length === 1 ? '' : 's'}`,
      );
    }
    if (otherPhones.length > 0) {
      parts.push(
        `${otherPhones.length} other phone number${otherPhones.length === 1 ? '' : 's'}`,
      );
    }
    bullets.push(
      `From this same device, prior applications used ${parts.join(' and ')} that are different from this application.`,
    );
  } else if ((onboarding.priorApplicationCount ?? 0) > 0) {
    bullets.push('This visitor has prior applications on the same device, with matching contact details so far.');
  }

  if (c.attemptedEmailMatchesOnFile === false) {
    bullets.push('The email they tried to use does not match the email on this application.');
  }
  if (c.attemptedPhoneMatchesOnFile === false) {
    bullets.push('The phone they tried to use does not match the phone on this application.');
  }
  if (c.otpEmailMatchesOnFile === false || c.otpPhoneMatchesOnFile === false) {
    bullets.push('A verification code was sent to a contact that does not match what is on file.');
  }

  const otp = onboarding.otpAttempts;
  const otpFailed = (otp?.channels ?? []).some((ch) => ch.verifyFailed > 0 || ch.lockedSessions > 0);
  const otpOk = (otp?.channels ?? []).some((ch) => ch.verifySuccess > 0) || Boolean(onboarding.phoneVerifiedAt);
  if (otpFailed) {
    bullets.push('Phone or email verification had failed or locked attempts on this application.');
  } else if (otpOk) {
    bullets.push('Phone verification completed successfully.');
  }

  if (reviewEvents.length > 0) {
    bullets.push(
      `${reviewEvents.length} sign-in or contact event${reviewEvents.length === 1 ? '' : 's'} on this application need a closer look.`,
    );
  }

  const needsReview = inventoryReview.length > 0 || reviewEvents.length > 0 || otpFailed
    || c.attemptedEmailMatchesOnFile === false
    || c.attemptedPhoneMatchesOnFile === false
    || c.otpEmailMatchesOnFile === false
    || c.otpPhoneMatchesOnFile === false;

  if (needsReview) {
    return {
      headline:
        otherEmails.length + otherPhones.length > 0
          ? 'This customer may have more than one account — different contact details showed up on the same device.'
          : 'Account access needs a closer look before trusting these contact details.',
      tone: 'warning',
      bullets: bullets.slice(0, 4),
    };
  }

  return {
    headline: otpOk
      ? 'Account access looks consistent — the customer verified their contact details.'
      : 'Account access is still in progress — no contact mismatches stood out yet.',
    tone: otpOk ? 'success' : 'info',
    bullets: bullets.length > 0 ? bullets.slice(0, 4) : ['No conflicting emails or phones found on this device so far.'],
  };
}

/** Latest identity session in plain language — prefers the duplicate → OTP story when present. */
export function buildIdentityTabNarrative(
  rowsOrAttempts: IdentityLookupRow[] | IdentityAttemptPresentation[],
): TabNarrative {
  const attempts: IdentityAttemptPresentation[] =
    rowsOrAttempts.length > 0 && 'role' in rowsOrAttempts[0]!
      ? (rowsOrAttempts as IdentityAttemptPresentation[])
      : presentIdentityAttempts(rowsOrAttempts as IdentityLookupRow[]);

  if (attempts.length === 0) {
    return {
      headline: 'Identity verification has not started yet.',
      tone: 'info',
      detail: 'The customer still needs to complete ID and selfie checks.',
      bullets: [],
    };
  }

  const hasExisting = attempts.some((a) => a.role === 'existing_found');
  const afterSignin = attempts.find((a) => a.role === 'after_signin');
  const latest = attempts[0]!;
  const bullets: string[] = [];

  // Mitek ran on any attempt (new / duplicate / returning re-verify).
  const mitekAttempts = attempts.filter((a) => a.row.mitekCalled);
  const mitekFail = mitekAttempts
    .map((a) => ({ attempt: a, outcome: resolveDocumentAuthenticityOutcome(a.row) }))
    .find((x) => x.outcome?.tone === 'error');
  const mitekReview = mitekAttempts
    .map((a) => ({ attempt: a, outcome: resolveDocumentAuthenticityOutcome(a.row) }))
    .find((x) => x.outcome?.tone === 'warning');
  const mitekPass = mitekAttempts
    .map((a) => ({ attempt: a, outcome: resolveDocumentAuthenticityOutcome(a.row) }))
    .find((x) => x.outcome?.tone === 'success');

  if (mitekFail?.outcome) {
    const copy = mitekAuthenticityOperatorCopy(mitekFail.attempt.row, 'hard_fail');
    bullets.push(copy.action);
  } else if (mitekReview?.outcome) {
    const copy = mitekAuthenticityOperatorCopy(mitekReview.attempt.row, 'review');
    bullets.push(copy.action);
  }

  // Returning selfie-only: no new ID / Mitek when on-file expiry is beyond the 30-day buffer.
  if (
    afterSignin &&
    !afterSignin.row.mitekCalled &&
    String(afterSignin.row.verification_route ?? '').toUpperCase() === 'RETURNING_SELFIE_1TO1'
  ) {
    bullets.push(
      'Returning path used selfie only — new ID photos and Mitek were skipped because the on-file document is not due for re-check (expiry more than 30 days out).',
    );
  } else if (
    afterSignin?.row.mitekCalled ||
    (hasExisting && mitekAttempts.some((a) => a.role !== 'after_signin' && a.row.mitekCalled))
  ) {
    if (!mitekFail) {
      bullets.push(
        'ID photos were collected and Mitek ran — typically because the on-file document is expired or within 30 days of expiry, or this was a full ID upload.',
      );
    }
  }

  // Two-step story: existing customer hit → OTP / after sign-in.
  if (hasExisting && afterSignin) {
    const afterSummary = resolveIdentitySessionSummary(afterSignin.row);
    if (afterSummary.tone === 'error' || mitekFail) {
      const failCopy = mitekFail
        ? mitekAuthenticityOperatorCopy(mitekFail.attempt.row, 'hard_fail')
        : null;
      return {
        headline: failCopy
          ? failCopy.title
          : 'Existing customer matched, but checks failed after sign-in',
        tone: 'error',
        detail: afterSummary.tone === 'error' ? afterSummary.body : failCopy?.body ?? null,
        bullets: bullets.slice(0, 4),
      };
    }
    if (mitekReview) {
      const reviewCopy = mitekAuthenticityOperatorCopy(mitekReview.attempt.row, 'review');
      return {
        headline: 'Existing customer matched — ID document needs manual review',
        tone: 'warning',
        detail: reviewCopy.body,
        bullets: bullets.slice(0, 4),
      };
    }
    return {
      headline: 'Existing customer matched, then signed in with OTP',
      tone: 'info',
      detail:
        'Identity first flagged a person already on file. After OTP sign-in, checks confirmed the same account.',
      bullets: bullets.slice(0, 4),
    };
  }

  if (hasExisting && !afterSignin) {
    const existing = attempts.find((a) => a.role === 'existing_found')!;
    const summary = resolveIdentitySessionSummary(existing.row);
    return {
      headline: 'Existing customer found — sign-in required',
      tone: mitekFail ? 'error' : 'warning',
      detail:
        summary.body ||
        'This submission matched someone already on file. The customer needs to sign in before continuing.',
      bullets: bullets.slice(0, 4),
    };
  }

  const summary = resolveIdentitySessionSummary(latest.row);
  let tone: TabNarrativeTone =
    summary.tone === 'success'
      ? 'success'
      : summary.tone === 'error'
        ? 'error'
        : summary.tone === 'warning'
          ? 'warning'
          : 'info';
  if (mitekFail) tone = 'error';
  else if (mitekReview) tone = 'warning';
  else if (tone === 'success' && bullets.length > 0) tone = 'info';

  const older =
    attempts.length > 1
      ? attempts.length === 2
        ? ' One older attempt is on file.'
        : ` ${attempts.length - 1} older attempts are on file.`
      : '';

  const expiredDocument =
    String(latest.row.session_status ?? '').toUpperCase() === 'FAILED_EXPIRED' ||
    /document expired/i.test(summary.title);

  // Authenticity and expiry are different gates. On an expired-ID stop, lead with
  // the OCR expiry date — do not imply the overall identity check "passed".
  if (expiredDocument) {
    const expiry = formatDocumentExpiryDate(latest.row.expiry_date);
    if (expiry && !bullets.some((b) => /expir/i.test(b))) {
      bullets.unshift(`Expiry date read from the ID front: ${expiry}.`);
    }
  } else if (
    mitekPass &&
    !mitekFail &&
    !mitekReview &&
    !bullets.some((b) => /Mitek ran/i.test(b))
  ) {
    bullets.push('Mitek document authenticity passed on the submitted ID images.');
  }

  const mitekFailCopy = mitekFail
    ? mitekAuthenticityOperatorCopy(mitekFail.attempt.row, 'hard_fail')
    : null;
  const mitekReviewCopy = mitekReview
    ? mitekAuthenticityOperatorCopy(mitekReview.attempt.row, 'review')
    : null;

  const mitekDetail = mitekFailCopy?.body ?? mitekReviewCopy?.body ?? null;

  return {
    headline: mitekFailCopy
      ? mitekFailCopy.title
      : mitekReviewCopy
        ? mitekReviewCopy.title
        : summary.title,
    tone,
    detail: mitekDetail ?? `${summary.body}${older}`.trim(),
    bullets: bullets.slice(0, 4),
  };
}

/** Prepends linkage funding context when legacy customer link blocks disbursement. */
export function enrichIdentityNarrativeWithLinkage(
  narrative: TabNarrative,
  linkage: LegacyLinkageJourney | null | undefined,
): TabNarrative {
  const alert = resolveLinkageOverviewAlert(linkage);
  if (!alert) return narrative;

  const linkageTone: TabNarrativeTone =
    alert.severity === 'error' ? 'error' : alert.severity === 'warning' ? 'warning' : 'info';

  const linkageBullet =
    normalizeLinkageStatus(linkage) === 'PENDING_REVIEW'
      ? 'Funding is waiting on legacy customer link — review the match panel below.'
      : alert.detail;

  return {
    ...narrative,
    tone:
      narrative.tone === 'success' || narrative.tone === 'info'
        ? linkageTone
        : narrative.tone,
    bullets: [linkageBullet, ...narrative.bullets].slice(0, 4),
  };
}

function readIncomeItems(data: unknown): Record<string, unknown>[] {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  const raw = (data as { items?: unknown }).items;
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is Record<string, unknown> => x != null && typeof x === 'object' && !Array.isArray(x));
}

function incomeDeclineReasonPhrase(input: {
  declineReason?: string | null;
  issueBit?: string | null;
  lastActionFailSummary?: string | null;
}): string {
  const decline = (input.declineReason ?? '').trim().toUpperCase();
  if (decline === 'INCOME_INSUFFICIENT' || /INCOME.*INSUFFICIENT/.test(decline)) {
    return 'insufficient income';
  }

  const corpus = `${decline} ${input.issueBit ?? ''} ${input.lastActionFailSummary ?? ''}`;
  if (/INSUFFICIENT|NOT ENOUGH|\$0(\.00)?\/MO|NOT ENOUGH FOR THIS PRODUCT/i.test(corpus)) {
    return 'insufficient income';
  }

  if (input.issueBit) {
    const bit = input.issueBit.replace(/\.$/, '').trim();
    if (/not enough|insufficient/i.test(bit)) return 'insufficient income';
    return bit.charAt(0).toLowerCase() + bit.slice(1);
  }

  return 'verification checks not passing';
}

export function buildIncomeTabNarrative(
  data: unknown | null,
  lastActionFailSummary: string | null,
  context?: {
    applicationId?: string;
    applicationStatus?: string;
    declineReason?: string | null;
  },
): TabNarrative {
  const items = readIncomeItems(data);
  if (items.length === 0) {
    return {
      headline: 'Income linking has not finished yet.',
      tone: 'info',
      detail: 'No bank income results are on file for this application.',
      bullets: [],
    };
  }

  const latest = items[0]!;
  const status = String(latest.finalStatus ?? latest.status ?? '').toUpperCase();
  const metrics = (latest.metrics && typeof latest.metrics === 'object'
    ? (latest.metrics as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const failReasons = uniquePlainIncomeFailReasons(
    Array.isArray(latest.failReasons)
      ? latest.failReasons
      : Array.isArray(metrics.failReasons)
        ? (metrics.failReasons as unknown[])
        : [],
  );
  const income =
    typeof metrics.normalizedMonthlyIncome === 'number'
      ? metrics.normalizedMonthlyIncome
      : null;
  // Amount lives on the attempt card — banner keeps outcome + issues only.
  const issueBit = failReasons[0] ?? (lastActionFailSummary
    ? uniquePlainIncomeFailReasons([lastActionFailSummary])[0] ?? null
    : null);
  const detail = issueBit;
  const appStatus = String(context?.applicationStatus ?? '').toUpperCase();
  const appId = context?.applicationId?.trim() || null;
  const declined = appStatus === 'DECLINED';

  if (/FAIL|REJECT|DECLIN|INSUFFICIENT/.test(status)) {
    if (declined && appId) {
      const reasonPhrase = incomeDeclineReasonPhrase({
        declineReason: context?.declineReason,
        issueBit,
        lastActionFailSummary,
      });
      return {
        headline: `Application ${appId} was declined — Income Verification failed due to ${reasonPhrase}`,
        tone: 'error',
        detail: null,
        bullets: [],
      };
    }
    return {
      headline: 'Income verification did not pass.',
      tone: 'error',
      detail,
      bullets: [],
    };
  }
  if (/REVIEW|PENDING|WARN|MANUAL/.test(status)) {
    return {
      headline: 'Income verification needs a closer look.',
      tone: 'warning',
      detail,
      bullets: [],
    };
  }
  if (/PASS|SUCCESS|VERIFIED|OK/.test(status) || income != null) {
    return {
      headline: 'Income linking completed.',
      tone: 'success',
      detail,
      bullets: [],
    };
  }
  return {
    headline: 'Income verification is in progress.',
    tone: 'info',
    detail,
    bullets: [],
  };
}

export function buildDecisionTabNarrative(input: {
  decisionEngine?: DecisionEngineJourneyDto | null;
  declineReason?: string | null;
  applicationStatus?: string;
  executions: ApplicationJourneyExecutionDto[];
}): TabNarrative {
  const de = input.decisionEngine;
  const decision = String(de?.decision ?? '').toUpperCase();
  const declinePlain = declineReasonPlain(input.declineReason);
  const status = String(input.applicationStatus ?? '').toUpperCase();

  const existingLoanHint = /RFSC|existing loan|not paying|delinquen|past due|arrears/i.test(
    `${input.declineReason ?? ''} ${de?.declinedExplanation ?? ''} ${de?.opLoanStatus ?? ''}`,
  );

  if (/DECLIN/.test(decision) || status === 'DECLINED') {
    return {
      headline: existingLoanHint
        ? 'Not eligible — existing loan issue.'
        : 'Credit decision declined this application.',
      tone: 'error',
      detail: existingLoanHint
        ? 'An existing loan problem was found (for example a loan that is not being paid on time).'
        : declinePlain,
      bullets: [],
    };
  }
  if (/APPROV/.test(decision)) {
    return {
      headline: 'Credit / capacity check approved.',
      tone: 'success',
      detail:
        typeof de?.maxEligibleAmount === 'number'
          ? `Max eligible amount: $${de.maxEligibleAmount.toLocaleString()}.`
          : null,
      bullets: [],
    };
  }
  if (/REFER|REVIEW|CHALLENGE/.test(decision)) {
    return {
      headline: 'Credit / capacity check needs a human review.',
      tone: 'warning',
      detail: null,
      bullets: [],
    };
  }
  if (de?.evaluationId || decision) {
    return {
      headline: 'A credit / capacity decision is on file.',
      tone: 'info',
      detail: null,
      bullets: [],
    };
  }
  if (input.executions.length > 0) {
    const latest = input.executions[0]!;
    const outcome = String(latest.outcome ?? '').toUpperCase();
    if (outcome === 'FAIL') {
      return {
        headline: 'Automated decision checks did not pass.',
        tone: 'error',
        detail: null,
        bullets: [],
      };
    }
    if (outcome === 'REVIEW' || outcome === 'CHALLENGE') {
      return {
        headline: 'Automated decision checks need a human review.',
        tone: 'warning',
        detail: null,
        bullets: [],
      };
    }
    return {
      headline: 'Automated decision checks have run.',
      tone: 'info',
      detail: null,
      bullets: [],
    };
  }
  return {
    headline: 'No credit decision recorded yet.',
    tone: 'info',
    detail: 'This usually runs after income is verified.',
    bullets: [],
  };
}

export function buildEmployerTabNarrative(input: {
  application?: ApplicationJourneyHeaderDto;
  latestStatus?: string | null;
  employerName?: string | null;
  failReason?: string | null;
  reviewSignals?: EmploymentReviewSignalsDto | null;
  footprint?: EmployerFootprintAdminViewDto | null;
}): TabNarrative {
  const app = input.application;
  const milestones = app?.completedMilestones;
  const status = String(input.latestStatus ?? '').toUpperCase();
  const name = input.employerName?.trim() || null;
  const fail = input.failReason?.trim() || null;

  if (
    app &&
    isEmploymentForwardedUnderReview({
      milestones,
      lastActionResult: app.lastActionResult,
    })
  ) {
    const review = buildEmploymentUnderReviewNarrative({
      lastActionResult: app.lastActionResult,
      failReason: fail,
      employerName: name,
      reviewSignals: input.reviewSignals ?? app.employmentReviewSignals,
      footprint: input.footprint,
    });
    return {
      headline: review.headline,
      tone: 'warning',
      detail: review.detail,
      bullets: [],
    };
  }

  if (hasEmploymentMilestone(milestones, 'EMPLOYMENT_VERIFIED') || /VERIFIED|PASS|SUCCESS/.test(status)) {
    return {
      headline: name ? `Employment verified for ${name}.` : 'Employment verification completed.',
      tone: 'success',
      detail: null,
      bullets: [],
    };
  }

  if (/REJECT|FAIL|DENIED/.test(status) || (fail && !hasEmploymentMilestone(milestones, 'EMPLOYMENT_PENDING_REVIEW'))) {
    if (/REJECT|FAIL|DENIED/.test(status) || fail) {
      return {
        headline: 'Employment could not be confirmed.',
        tone: 'error',
        detail: fail ? fail.replace(/_/g, ' ') : name ? `Employer attempted: ${name}.` : null,
        bullets: [],
      };
    }
  }

  if (hasEmploymentMilestone(milestones, 'EMPLOYMENT_PENDING_REVIEW') || /REVIEW|MANUAL|PENDING/.test(status)) {
    return {
      headline: 'Employment still needs a closer look.',
      tone: 'warning',
      detail: name ? `Employer on file: ${name}.` : null,
      bullets: [],
    };
  }

  const phase = app
    ? resolveEmploymentAdminPhase({
        milestones,
        applicationStatus: app.status,
        lastAction: app.lastAction,
        lastActionResult: app.lastActionResult,
        latestFinalStatus: input.latestStatus ?? null,
      })
    : 'not_started';

  if (phase === 'not_started') {
    return {
      headline: 'Employment verification has not started yet.',
      tone: 'info',
      detail: null,
      bullets: [],
    };
  }

  return {
    headline: 'Employment verification is in progress.',
    tone: 'info',
    detail: name ? `Looking at employer: ${name}.` : null,
    bullets: [],
  };
}
